import { AnimatePresence, motion } from "framer-motion";
import type React from "react";
import { useEffect } from "react";
import { LinkifiedDescription } from "./LinkifiedDescriptions";

interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  createdAt?: string;
}

const formatTime = (isoString?: string) => {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    return d.toLocaleString("hu-HU", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return "";
  }
};

export const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  createdAt,
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleEscape);

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md"
          onClick={onClose}
        >
          {/* Backdrop wrapper to handle click-away correctly */}
          <div className="absolute inset-0" onClick={onClose} />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            transition={{ type: "spring", duration: 0.4 }}
            className="w-full max-w-[400px] rounded-2xl border border-white/10 shadow-[0_25px_60px_rgba(0,0,0,0.85)] bg-[#1a1a1a]/95 flex flex-col overflow-hidden relative z-10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Ambient Background Glow */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#22D3BA]/10 to-[#F7C4FF]/10 blur-2xl pointer-events-none" />

            {/* Header: Support-band's masking-tape-gradient */}
            <div className="masking-tape-gradient px-6 py-4 border-b border-white/10 flex justify-between items-center relative z-10 text-[#0F0F0F]">
              <div className="flex flex-col relative z-20">
                <h2 className="font-['Unbounded'] text-sm font-black uppercase tracking-wider">
                  {title || "Értesítés"}
                </h2>
                {createdAt && (
                  <span className="text-[10px] text-[#0F0F0F]/80 font-bold mt-1">
                    {formatTime(createdAt)}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-[#0F0F0F]/70 hover:text-[#0F0F0F] font-bold p-1 relative z-20 transition-colors"
                aria-label="Bezárás"
              >
                ✕
              </button>
            </div>

            {/* Content Box: More Transparency */}
            <div className="bg-black/40 px-6 py-6 relative z-10 flex-1">
              {message ? (
                <LinkifiedDescription
                  description={message}
                  className="text-sm text-white font-medium leading-relaxed whitespace-pre-wrap break-words"
                />
              ) : (
                <p className="text-sm text-muted italic text-center py-4">Nincs új értesítés.</p>
              )}
            </div>

            {/* Footer with Close Button */}
            <div className="bg-black/20 px-6 py-4 border-t border-white/5 relative z-10">
              <button
                type="button"
                onClick={onClose}
                className="w-full bg-main text-[#222222] font-['Unbounded'] font-bold text-xs py-2.5 rounded-xl hover:brightness-110 active:scale-[0.98] transition-all uppercase tracking-wider"
              >
                Bezárás
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationModal;
